/**
 * ReservePay Payment API Client & React Query Hooks
 * Created: 2026-06-13
 *
 * API calls for ReservePay hosted checkout (PromptPay-first).
 * Follows the same pattern as package-purchase.ts and useAddCredit.ts.
 */

import { useMutation } from '@tanstack/react-query'
import {
  ReservePayCreditCheckoutResponse,
  ReservePayPackageCheckoutResponse,
  ReservePayInitiateCreditFlowResponse,
  ReservePayInitiatePackageFlowResponse,
  PaymentStatusResponse,
  RESERVEPAY_PENDING_TXN_KEY,
  RESERVEPAY_PENDING_TYPE_KEY,
} from '@/src/types/reservepay-payment'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// ============================================================================
// Helpers
// ============================================================================

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  const data = await res.json()
  if (!res.ok || !data.success) {
    const err: Error & { code?: string } = new Error(
      data.error || data.message || 'Request failed'
    )
    err.code = data.code
    throw err
  }
  return data as T
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Create ReservePay hosted checkout for credit top-up.
 * Returns checkout URL to redirect user to.
 */
export async function createCreditTopupReservePayCheckout(
  amount: number,
  returnUrl?: string
): Promise<ReservePayCreditCheckoutResponse> {
  const res = await fetch(`${API_BASE_URL}/api/payment/user/credit/create-reservepay-checkout`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ amount, ...(returnUrl ? { returnUrl } : {}) }),
  })
  return handleResponse<ReservePayCreditCheckoutResponse>(res)
}

/**
 * Create ReservePay hosted checkout for package purchase.
 * Returns checkout URL to redirect user to.
 */
export async function createPackageReservePayCheckout(
  packageId: string,
  activationChoice: 'immediate' | 'queued' = 'queued',
  returnUrl?: string
): Promise<ReservePayPackageCheckoutResponse> {
  const res = await fetch(
    `${API_BASE_URL}/api/payment/user/package/create-reservepay-checkout`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        packageId,
        activationChoice,
        ...(returnUrl ? { returnUrl } : {}),
      }),
    }
  )
  return handleResponse<ReservePayPackageCheckoutResponse>(res)
}

/**
 * Get payment transaction status.
 * Used for polling after returning from ReservePay checkout.
 */
export async function getPaymentStatus(transactionId: string): Promise<PaymentStatusResponse> {
  const token = localStorage.getItem('authToken')
  const res = await fetch(`${API_BASE_URL}/api/payment/user/status/${transactionId}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  return handleResponse<PaymentStatusResponse>(res)
}

// ============================================================================
// Local Storage Helpers (localStorage — survives deeplink / bank app redirects)
// ============================================================================

/**
 * Store pending ReservePay transaction ID before redirecting user to checkout.
 */
export function storePendingTransaction(transactionId: string, type: 'credit' | 'package') {
  localStorage.setItem(RESERVEPAY_PENDING_TXN_KEY, transactionId)
  localStorage.setItem(RESERVEPAY_PENDING_TYPE_KEY, type)
}

/**
 * Retrieve and clear pending transaction ID on return from checkout.
 */
export function consumePendingTransaction(): { txid: string; type: 'credit' | 'package' } | null {
  const txid = localStorage.getItem(RESERVEPAY_PENDING_TXN_KEY)
  const type = localStorage.getItem(RESERVEPAY_PENDING_TYPE_KEY) as 'credit' | 'package' | null
  if (!txid) return null
  localStorage.removeItem(RESERVEPAY_PENDING_TXN_KEY)
  localStorage.removeItem(RESERVEPAY_PENDING_TYPE_KEY)
  return { txid, type: type ?? 'credit' }
}

/**
 * Redirect user to ReservePay checkout URL.
 * Stores the transaction ID so /payment/return can poll status.
 */
export function redirectToReservePayCheckout(
  checkoutUrl: string,
  transactionId: string,
  type: 'credit' | 'package'
) {
  storePendingTransaction(transactionId, type)
  window.location.href = checkoutUrl
}

// ============================================================================
// React Query Hooks
// ============================================================================

export function useCreateCreditReservePayCheckout() {
  return useMutation<
    ReservePayCreditCheckoutResponse,
    Error & { code?: string },
    { amount: number; returnUrl?: string }
  >({
    mutationFn: ({ amount, returnUrl }) =>
      createCreditTopupReservePayCheckout(amount, returnUrl),
  })
}

export function useCreatePackageReservePayCheckout() {
  return useMutation<
    ReservePayPackageCheckoutResponse,
    Error & { code?: string },
    { packageId: string; activationChoice?: 'immediate' | 'queued'; returnUrl?: string }
  >({
    mutationFn: ({ packageId, activationChoice, returnUrl }) =>
      createPackageReservePayCheckout(packageId, activationChoice ?? 'queued', returnUrl),
  })
}

// ============================================================================
// Custom UI Flow — initiate-reservepay-flow (QR shown in-app)
// ============================================================================

/**
 * Backend: create DB transaction + call initiate-payment-flow with payment_session_id.
 * Returns payment_id used for SDK polling.
 */
export async function initiateCreditReservePayFlow(
  paymentSessionId: string,
  amount: number,
  opts?: { returnUrl?: string; paymentMethodScope?: 'promptpay' | 'card' | 'mobile_banking' | 'true_money' | 'alipay' | 'wechat' }
): Promise<ReservePayInitiateCreditFlowResponse> {
  const res = await fetch(`${API_BASE_URL}/api/payment/user/credit/initiate-reservepay-flow`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      payment_session_id: paymentSessionId,
      amount,
      ...(opts?.returnUrl ? { returnUrl: opts.returnUrl } : {}),
      ...(opts?.paymentMethodScope ? { payment_method_scope: opts.paymentMethodScope } : {}),
    }),
  })
  return handleResponse<ReservePayInitiateCreditFlowResponse>(res)
}

export async function initiatePackageReservePayFlow(
  paymentSessionId: string,
  packageId: string,
  activationChoice: 'immediate' | 'queued' = 'queued',
  opts?: { returnUrl?: string; paymentMethodScope?: 'promptpay' | 'card' | 'mobile_banking' | 'true_money' | 'alipay' | 'wechat' }
): Promise<ReservePayInitiatePackageFlowResponse> {
  const res = await fetch(`${API_BASE_URL}/api/payment/user/package/initiate-reservepay-flow`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      payment_session_id: paymentSessionId,
      packageId,
      activationChoice,
      ...(opts?.returnUrl ? { returnUrl: opts.returnUrl } : {}),
      ...(opts?.paymentMethodScope ? { payment_method_scope: opts.paymentMethodScope } : {}),
    }),
  })
  return handleResponse<ReservePayInitiatePackageFlowResponse>(res)
}
